/**
 * KeyboardGeocoder
 *
 * A thin wrapper around <Geocoder> (react-mapbox-gl-geocoder) that adds
 * keyboard navigation to the suggestion dropdown:
 *   - ArrowDown / ArrowUp  — move highlight through results (wraps)
 *   - Enter                — select the highlighted result
 *   - Escape               — clear highlight
 *
 * The underlying library renders the suggestions as plain
 * <div class="react-geocoder-item"> nodes with an onClick handler. We use
 * a ref to the wrapper div to find those nodes, maintain the highlighted
 * index in a ref (so we don't need to re-render to sync the DOM), apply a
 * `highlighted` class for visual feedback, and trigger the item's native
 * click when the user presses Enter.
 */
import React, { useRef, useEffect } from 'react';
import Geocoder from 'react-mapbox-gl-geocoder';

const ITEM_SELECTOR = '.react-geocoder-item';
const HIGHLIGHT_CLASS = 'rgc-item-highlighted';

const KeyboardGeocoder = props => {
  const containerRef = useRef(null);
  const indexRef = useRef(-1);

  const getItems = () => {
    if (!containerRef.current) return [];
    return Array.from(containerRef.current.querySelectorAll(ITEM_SELECTOR));
  };

  const applyHighlight = () => {
    const items = getItems();
    items.forEach((item, idx) => {
      if (idx === indexRef.current) {
        item.classList.add(HIGHLIGHT_CLASS);
        // Keep the highlighted item in view inside the dropdown
        if (typeof item.scrollIntoView === 'function') {
          item.scrollIntoView({ block: 'nearest' });
        }
      } else {
        item.classList.remove(HIGHLIGHT_CLASS);
      }
    });
  };

  const resetHighlight = () => {
    indexRef.current = -1;
    applyHighlight();
  };

  const handleKeyDown = e => {
    const items = getItems();
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      indexRef.current = (indexRef.current + 1) % items.length;
      applyHighlight();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      indexRef.current =
        indexRef.current <= 0 ? items.length - 1 : indexRef.current - 1;
      applyHighlight();
    } else if (e.key === 'Enter') {
      if (indexRef.current >= 0 && items[indexRef.current]) {
        e.preventDefault();
        items[indexRef.current].click();
        resetHighlight();
      }
    } else if (e.key === 'Escape') {
      resetHighlight();
    }
  };

  // Any text input in the search box means the user is typing a new query,
  // so previous results (and their highlight) are stale.
  const handleInput = () => {
    indexRef.current = -1;
  };

  // If the dropdown is closed (items removed from the DOM), drop the stale
  // index so it doesn't apply to a future dropdown.
  useEffect(() => {
    if (!containerRef.current) return undefined;
    const observer = new MutationObserver(() => {
      if (getItems().length === 0) {
        indexRef.current = -1;
      }
    });
    observer.observe(containerRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      onKeyDown={handleKeyDown}
      onInput={handleInput}
      // Hint to assistive tech that this is a combobox-style widget
      role="combobox"
      aria-haspopup="listbox"
    >
      <Geocoder {...props} />
    </div>
  );
};

export default KeyboardGeocoder;
